/*
 * Fetching the routing graph's files (scripts/routing/FORMAT.md), and keeping
 * them on the device so a route can be planned again with no signal.
 *
 *   routing/current.json        always asked afresh (cache: no-store); the
 *                               last one seen is remembered for offline use
 *   <version>/manifest.json     then the trunk and the tiles near the start
 *   <version>/trunk.bin.gz      and the target -- each checked against its
 *   <version>/tiles/...         sha256 and kept in Cache Storage
 *                               ('pitriot-routing-<version>'; the service
 *                               worker leaves that cache alone)
 *
 * Every dependency (fetch, the store, the hash) is passed in, so this runs
 * in the routing worker, on the main thread, and in the tests alike.
 */
const LAST_POINTER = 'mb_routing_pointer';
export const CACHE_PREFIX = 'pitriot-routing-';

export class RoutingUnavailable extends Error {}

/** The tiles whose cell lies within `radiusKm` of any of `points`. */
export function tilesFor(manifest, points, radiusKm) {
  const dLat = radiusKm / 111.32;
  return (manifest.tiles || []).filter((t) => {
    const [south, west, north, east] = t.bbox;
    return points.some(({ lat, lng }) => {
      const dLng = radiusKm / (111.32 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)));
      return lat + dLat >= south && lat - dLat <= north && lng + dLng >= west && lng - dLng <= east;
    });
  });
}

export async function sha256Hex(bytes) {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  const digest = await subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** A store of fetched files: Cache Storage when there is one, else memory. */
export function deviceStore(version) {
  const memory = new Map();
  const cachesApi = typeof caches !== 'undefined' ? caches : null;
  const name = `${CACHE_PREFIX}${version}`;
  return {
    async get(url) {
      if (memory.has(url)) return memory.get(url);
      if (!cachesApi) return null;
      try {
        const hit = await (await cachesApi.open(name)).match(url);
        return hit ? hit.arrayBuffer() : null;
      } catch (e) {
        return null;
      }
    },
    async put(url, bytes) {
      memory.set(url, bytes);
      if (!cachesApi) return;
      try {
        await (await cachesApi.open(name)).put(url, new Response(bytes.slice(0)));
        // An older version's files are of no more use.
        for (const old of await cachesApi.keys()) {
          if (old.startsWith(CACHE_PREFIX) && old !== name) await cachesApi.delete(old);
        }
      } catch (e) { /* quota or a private window: kept in memory for this session */ }
    },
  };
}

function remembered(storage) {
  try {
    return JSON.parse(storage?.getItem(LAST_POINTER) || 'null');
  } catch (e) {
    return null;
  }
}

/** {version, manifestUrl} from the pointer file, or the last one seen when offline. */
export async function readPointer(pointerUrl, { fetchFn = fetch, storage = globalThis.localStorage } = {}) {
  try {
    const res = await fetchFn(pointerUrl, { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const pointer = await res.json();
    if (pointer?.format !== 'pitriot-routing' || !pointer.version || !pointer.manifest) throw new Error('not a pointer');
    const out = { version: pointer.version, manifestUrl: new URL(pointer.manifest, new URL(pointerUrl, baseHref())).href };
    try { storage?.setItem(LAST_POINTER, JSON.stringify(out)); } catch (e) { /* nothing kept */ }
    return out;
  } catch (e) {
    const last = remembered(storage);
    if (last?.version && last?.manifestUrl) return last;
    throw new RoutingUnavailable('no routing data on this device or the server');
  }
}

function baseHref() {
  return typeof location !== 'undefined' && location.href ? location.href : 'http://localhost/';
}

/** One file's bytes: from the store, else fetched (and checked, and kept). */
export async function fetchFile(url, { sha256, store, fetchFn = fetch, hash = sha256Hex }) {
  const kept = await store.get(url);
  if (kept) return kept;
  let res;
  try {
    res = await fetchFn(url);
  } catch (e) {
    throw new RoutingUnavailable(`cannot fetch ${url}`);
  }
  if (!res.ok) throw new RoutingUnavailable(`HTTP ${res.status} for ${url}`);
  const bytes = await res.arrayBuffer();
  if (sha256) {
    const got = await hash(bytes);
    if (got && got !== sha256) throw new Error(`${url} does not match its manifest`);
  }
  await store.put(url, bytes);
  return bytes;
}

export async function readManifest(manifestUrl, deps) {
  const bytes = await fetchFile(manifestUrl, deps);
  return JSON.parse(new TextDecoder().decode(bytes));
}

/** The URL of a manifest entry's file. */
export const fileUrl = (manifestUrl, file) => new URL(file, manifestUrl).href;
